/**
 * Format a phone number for display (international-friendly, NANP-aware).
 * Rules:
 * - Normalize leading "00" to "+".
 * - "+1" (11 digits) → "+1 (XXX) XXX-XXXX"; 10 digits → "(XXX) XXX-XXXX".
 * - Other 8–15 digits → "+<digits>". Otherwise returns trimmed input.
 * @param {string|number} phoneNumber Raw input (may include punctuation/spaces or "+"/"00" prefix).
 * @param {string} [defaultValue=''] Fallback when input is empty.
 * @returns {string}
 */
export const getFormattedPhoneNumber = (phoneNumber, defaultValue = '') => {
  if (!phoneNumber) return defaultValue;

  const trimmedPhoneNumberString = String(phoneNumber).trim();
  if (!trimmedPhoneNumberString) return defaultValue;

  let nonWhitespace = trimmedPhoneNumberString.replace(/\s+/g, '');
  if (nonWhitespace.startsWith('00'))
    nonWhitespace = `+${nonWhitespace.slice(2)}`;

  const hasPlus = nonWhitespace.startsWith('+');
  const digits = nonWhitespace.replace(/\D/g, '');

  /**
   * Format a North American Numbering Plan (NANP) phone number.
   * @param {string} nationalSignificantNumber - The phone number to format (National Significant Number).
   * @returns {string} The formatted phone number.
   */
  const formatNANP = (nationalSignificantNumber) => {
    if (nationalSignificantNumber.length >= 10) {
      return `(${nationalSignificantNumber.slice(0, 3)}) ${nationalSignificantNumber.slice(3, 6)}-${nationalSignificantNumber.slice(6, 10)}`;
    }
    if (nationalSignificantNumber.length === 7) {
      return `${nationalSignificantNumber.slice(0, 3)}-${nationalSignificantNumber.slice(3, 7)}`;
    }
    return nationalSignificantNumber;
  };

  if (hasPlus) {
    if (digits.startsWith('1') && digits.length === 11) {
      return `+1 ${formatNANP(digits.slice(1))}`;
    }
    return `+${digits}`;
  }

  // If the phone number is 10 digits, format it as a NANP phone number.
  if (digits.length === 10) return formatNANP(digits);

  // If the phone number is 11 digits and starts with 1, format the last 10 digits as a NANP phone number and prefix with +1.
  if (digits.length === 11 && digits[0] === '1') {
    return `+1 ${formatNANP(digits.slice(1))}`;
  }

  // If the phone number is between 8 and 15 digits, prefix with +.
  if (digits.length >= 8 && digits.length <= 15) return `+${digits}`;

  // Otherwise, return the trimmed phone number string.
  return trimmedPhoneNumberString;
};
